import AttendeeTicketNotification from '@eventbrite/attendee-ticket-notification';
import { Layout } from '@eventbrite/eds-layout';
import { isMediumNarrowDown } from '@eventbrite/eds-utils';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { checkGate, updateUser } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import React from 'react';
import { connect } from 'react-redux';
import { ConsumerHeader } from '../../../../components/ConsumerHeader';
import { GA_HOME_FEED_CATEGORY } from '../../../../constants/analytics';
import {
    GetEnrollmentStatus,
    useGetStatsigEnrollment,
} from '../../../../hooks';
import {
    Buckets,
    ConnectedProps,
    DiscoverState,
    Events,
    FeatureFlags,
    GASettings,
    PlaceObject,
} from '../../../../types/index';
import { isLocalStorageSupported } from '../../../../utils/localStorage';
import { BasePage } from '../../../base';
import withStickBehavior from '../../hoc/withStickyBehavior';
import { updateHeaderImage } from '../../redux/header';
import { getRestOfContext } from '../../redux/root';
import { runSearch } from '../../redux/search';
import { getFeedTrackingProperties } from '../../utils';
import { BrowseContainer } from '../BrowseContainer';
import './HomeBaseContainer.scss';

const HeaderContainer = loadable(() => import('../HeaderContainer'), {
    resolveComponent: (components: { HeaderContainer: React.ReactElement }) =>
        components.HeaderContainer,
    fallback: <div style={{ height: '300px' }}></div>,
});

const FullbleedHeaderContainer = loadable(
    () => import('../FullbleedHeaderContainer'),
    {
        resolveComponent: (components: {
            FullbleedHeaderContainer: React.ReactElement;
        }) => components.FullbleedHeaderContainer,
        fallback: <div className="fullbleed-header-fallback"></div>,
    },
);

const RecFeedbackContainer = loadable(() => import('../RecFeedbackContainer'));

const TutorialContainer = loadable(
    () => import('../TutorialContainer/TutorialContainer'),
);

const isFeatureFlagRecFeedbackStatusPass = checkGate('rec_feedback');

const EnhancedHomeConsumerHeader = withStickBehavior(ConsumerHeader);

const localStorageTutorialViewed =
    HAS_WINDOW &&
    isLocalStorageSupported() &&
    window.localStorage.getItem('search:tutorial:viewed') === 'true';

//Connect BasePage with extra content
const _mapStateToProps = (
    { app, user, content }: DiscoverState,
    ownProps: any,
) => ({
    ...ownProps,
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    featureFlags: app.featureFlags,
    seasonalContent: content.seasonal,
});

const _mapDispatchToProps = {
    handleSearch: runSearch,
};

const ConnectedBasePage = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(BasePage);

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl: string) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

type ExperimentValues = 'loading' | 0 | 1 | 2 | true | false;

interface OwnProps {
    buckets: Buckets;
    featureFlags?: FeatureFlags;
    location: Location;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    currentPlace?: string;
    currentPlaceParent?: string;
    locations?: {
        places: PlaceObject[];
    };
    placeType?: string;
    slug?: string;
    flatBucket?: {
        results: Events;
    };
    handleRestOfContext?: Function;
    originalContext?: object;
    history?: object;
    updateHeaderImage?: Function;
    enable_experimentation?: boolean;
    should_enable_experimentation?: boolean;
    experimentationEnabled?: boolean;
    isGDPRCountry?: boolean;
    gaSettings?: GASettings;
    tutorialExperimentEnabled?: ExperimentValues;
}

declare global {
    interface Window {
        evidon: any;
    }
}

interface HomeBaseContainerState {
    isInBrowser?: boolean;
    isMobile?: boolean;
    isLocalStorageRecFeedbackViewed?: boolean;
}

export class HomeBaseContainer extends React.Component<
    HomeBaseContainerProps,
    HomeBaseContainerState
> {
    constructor(props: HomeBaseContainerProps) {
        super(props);

        this.state = {
            isInBrowser: false,
            isMobile: false,
        };

        props.updateHeaderImage?.();
    }

    componentDidMount() {
        const {
            handleRestOfContext,
            originalContext,
            isAuthenticated,
            userId,
            tabKey,
        } = this.props;

        handleRestOfContext?.(originalContext, tabKey);

        this.setState({
            isInBrowser: true,
            isMobile: isMediumNarrowDown(),
        });

        const feedTrackingProperties = getFeedTrackingProperties(
            isAuthenticated,
            userId,
        );

        trackAnalyticsEvent(feedTrackingProperties);

        if (HAS_WINDOW) {
            this.setState({
                // set true if localStorage is supported and user already saw feedback form
                // also, set true if localStorage is NOT supported. We don't want to continually
                // show the feedback form.
                isLocalStorageRecFeedbackViewed: isLocalStorageSupported()
                    ? window?.localStorage?.getItem(
                          'search:recFeedback:viewed',
                      ) === 'true'
                    : true,
            });
        }
    }

    componentDidUpdate(prevProps: HomeBaseContainerProps) {
        const { app, userId } = this.props;
        const prevUserId = prevProps?.userId;

        //Only update user when the userId changes
        if (
            this.state.isInBrowser &&
            !app.loadingState?.isFeLoading &&
            userId !== prevUserId
        ) {
            updateUser({ user: { userID: userId } });
        }
    }

    canShowRecFeedbackForm() {
        return (
            isFeatureFlagRecFeedbackStatusPass &&
            this.props.tabKey === 'for_you' &&
            !this.state.isLocalStorageRecFeedbackViewed
        );
    }

    canShowTutorial() {
        return (
            HAS_WINDOW &&
            // this check fixes issue with cookie banner getting blocked by tutorial modal
            !window?.evidon?.banner?.isVisible() &&
            !localStorageTutorialViewed &&
            this.props.tutorialExperimentEnabled &&
            this.props.tabKey === 'for_you'
        );
    }

    render() {
        const { header } = this.props;
        const useNewHeader = header?.forceSpecialStyles;

        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        const overrideProps = this.state.isInBrowser
            ? {}
            : {
                  env: {
                      ...this.props.env,
                      loginUrl: fixLoginUrl(this.props.env?.loginUrl || ''),
                      signinUrl: fixLoginUrl(this.props.env?.signinUrl || ''),
                      signupUrl: fixLoginUrl(this.props.env?.signupUrl || ''),
                      showCookieHeader: false,
                  },
                  footer: <div />,
                  user: {
                      isAuthenticated: false,
                      publicId: null,
                      canCreateEvents: false,
                  },
              };

        return (
            <ConnectedBasePage
                {...this.props}
                {...overrideProps}
                isHomePage={true}
                structureHeader={EnhancedHomeConsumerHeader}
            >
                <AttendeeTicketNotification
                    shouldAnimateEntry={true}
                    isAuthenticated={this.props.isAuthenticated}
                    containerStyles={
                        this.state.isMobile
                            ? { margin: '0 auto', top: '120px' }
                            : { right: '32px', top: '120px' }
                    }
                    onLinkClick={(eid: string, hasDigitalContent: boolean) =>
                        trackAnalyticsEvent({
                            category: GA_HOME_FEED_CATEGORY,
                            label: eid,
                            action: hasDigitalContent
                                ? 'UpcomingOnlineEventViewed'
                                : 'UpcomingTicketViewed',
                        })
                    }
                    onDismiss={(
                        timeUntilEvent: string,
                        hasDigitalContent: boolean,
                    ) =>
                        trackAnalyticsEvent({
                            category: GA_HOME_FEED_CATEGORY,
                            label: timeUntilEvent,
                            action: hasDigitalContent
                                ? 'UpcomingOnlineEventDismissed'
                                : 'UpcomingTicketDismissed',
                        })
                    }
                    domNode=""
                    animateEntryDirection="downwards"
                />
                {useNewHeader && (
                    <FullbleedHeaderContainer history={this.props.history} />
                )}
                <Layout hasHorizontalGutters={true} maxWidth="large">
                    <>
                        {!useNewHeader && (
                            <HeaderContainer history={this.props.history} />
                        )}
                    </>
                    <BrowseContainer />
                </Layout>
                {this.canShowRecFeedbackForm() ? (
                    <RecFeedbackContainer />
                ) : null}
                {this.canShowTutorial() ? <TutorialContainer /> : null}
            </ConnectedBasePage>
        );
    }
}

const FunctionalHomeBaseContainerWrapper = (props: HomeBaseContainerProps) => {
    //The prop as defined in the server render (via ESI) is should_enable_experimentation
    //while the prop defined on the context blob on __SERVER_DATA__ is enable_experimentation.
    //Due to this mismatch and uncertainty on core releases, grabbing both.
    const { enable_experimentation, should_enable_experimentation } = props;

    //In case should_enable_experimentation is undefined
    //casting the value to a boolean
    const experimentationEnabled = !!(
        enable_experimentation || should_enable_experimentation
    );

    const tutorialExperiment = useGetStatsigEnrollment({
        name: 'personalization_tutorial',
        paramName: 'show_tutorial',
        defaultValue: false,
    });

    const parsedTutorialVariant =
        tutorialExperiment.status === GetEnrollmentStatus.SUCCESS
            ? tutorialExperiment.variant
            : GetEnrollmentStatus.LOADING;

    return (
        <HomeBaseContainer
            experimentationEnabled={experimentationEnabled}
            tutorialExperimentEnabled={
                parsedTutorialVariant as ExperimentValues
            }
            {...props}
        />
    );
};

const _mapHomeStateToProps = ({
    app,
    user,
    env,
    header,
    content,
}: DiscoverState) => ({
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    env,
    app,
    header,
    tabKey: content.browseState?.tabKey,
});

const _mapHomeDispatchToProps = {
    handleSearch: runSearch,
    handleRestOfContext: getRestOfContext,
    updateHeaderImage: updateHeaderImage,
};

type ReduxProps = ConnectedProps<
    typeof _mapHomeStateToProps,
    typeof _mapHomeDispatchToProps
>;
type HomeBaseContainerProps = ReduxProps & OwnProps;

export default connect(
    _mapHomeStateToProps,
    _mapHomeDispatchToProps,
)(FunctionalHomeBaseContainerWrapper);
